import {
  MONATE,
  euerRechnen,
  ustvaMonat,
  ustvaQuartal,
  ustvaRechnen,
  type UstvaWerte,
} from "./kern/rechnen";
import { taktAm } from "./kern/fristen";
import {
  Abschnitt,
  Hinweis,
  Tabelle,
  Zahl,
  fmtCent,
  fmtDatum,
  fmtGanz,
  type TabProps,
} from "./teile";

const UstvaZeile = ({
  name,
  w,
  fett,
}: {
  name: string;
  w: UstvaWerte;
  fett?: boolean;
}) => (
  <tr className={fett ? "font-semibold" : undefined}>
    <td className="whitespace-nowrap">{name}</td>
    <Zahl>{fmtGanz(w.kz81)}</Zahl>
    <Zahl>{fmtGanz(w.kz86)}</Zahl>
    <Zahl>{fmtCent(w.ust81 + w.ust86)}</Zahl>
    <Zahl>{fmtCent(w.kz66)}</Zahl>
    <Zahl>{fmtCent(w.zahllast)}</Zahl>
  </tr>
);

const KOPF = [
  "Zeitraum",
  "Kz 81 (19 %)",
  "Kz 86 (7 %)",
  "USt",
  "Kz 66",
  "Zahllast",
];

export const AuswertungTab = ({ basis, daten, posten, punkte }: TabProps) => {
  const { jahr } = basis;
  const jahrWerte = ustvaRechnen(posten, jahr);
  const euer = euerRechnen(posten, jahr);
  const takte = new Set(
    Array.from({ length: 12 }, (_, i) =>
      taktAm(daten.takte, `${jahr.jahr}-${String(i + 1).padStart(2, "0")}-01`),
    ),
  );
  const zeigeMonate = takte.has("monatlich") || takte.has("offen");
  const zeigeQuartale = takte.has("vierteljaehrlich") || takte.has("offen");
  const gruppen = [...new Set(punkte.map((p) => p.punkt))].map((punkt) => ({
    punkt,
    liste: punkte.filter((p) => p.punkt === punkt),
  }));

  return (
    <div className="space-y-4">
      <Abschnitt
        titel="Umsatzsteuer-Voranmeldung"
        text={`${jahr.besteuerung === "ist" ? "Ist" : "Soll"}-Versteuerung. Kz 81 und 86 in vollen Euro, Cent abgeschnitten; Quartale aus den Buchungen; Kz 66 mit Cent.`}
      >
        {takte.has("offen") && (
          <Hinweis art="warnung">
            Takt offen: Monate und Quartale stehen gleichwertig untereinander.
          </Hinweis>
        )}
        {!jahr.kur_verzicht && (
          <Hinweis art="warnung">
            Kleinunternehmerregelung aktiv, es entsteht keine USt.
          </Hinweis>
        )}
        <Tabelle kopf={KOPF} rechts={[1, 2, 3, 4, 5]}>
          {(zeigeMonate || !zeigeQuartale) &&
            MONATE.map((m, i) => (
              <UstvaZeile
                key={m}
                name={m}
                w={ustvaMonat(posten, jahr, i + 1)}
              />
            ))}
          {(zeigeQuartale || !zeigeMonate) &&
            [1, 2, 3, 4].map((q) => (
              <UstvaZeile
                key={q}
                name={`${q}. Quartal`}
                w={ustvaQuartal(posten, jahr, q)}
                fett
              />
            ))}
          <UstvaZeile name="Jahr gesamt" w={jahrWerte} fett />
        </Tabelle>
      </Abschnitt>

      <Abschnitt
        titel="§ 13b und innergemeinschaftlicher Erwerb"
        text="Prüfwerte aus den markierten Zeilen, nicht in Zahllast und Kz 66 oben eingerechnet. Steuer und Vorsteuer heben sich bei vollem Abzug auf."
      >
        <Tabelle
          kopf={["Fall", "Bemessung", "Steuer", "Vorsteuer"]}
          rechts={[1, 2, 3]}
        >
          <tr>
            <td>
              § 13b, EU-Leistende (Kz 46 / 47 / 67); außerhalb der EU Kz 84 / 85
            </td>
            <Zahl>{fmtCent(jahrWerte.bemessung13b)}</Zahl>
            <Zahl>{fmtCent(jahrWerte.ust13b)}</Zahl>
            <Zahl>{fmtCent(jahrWerte.ust13b)}</Zahl>
          </tr>
          <tr>
            <td>
              Innergemeinschaftlicher Erwerb 19 % (Kz 89 / 61); 7 %: Kz 93
            </td>
            <Zahl>{fmtCent(jahrWerte.bemessungIge)}</Zahl>
            <Zahl>{fmtCent(jahrWerte.ustIge)}</Zahl>
            <Zahl>{fmtCent(jahrWerte.ustIge)}</Zahl>
          </tr>
        </Tabelle>
      </Abschnitt>

      <Abschnitt
        titel="Einnahmenüberschussrechnung"
        text="Zufluss und Abfluss. Ausgaben stehen negativ."
      >
        <Tabelle kopf={["Position", "Betrag"]} rechts={[1]}>
          {euer.zeilen.map((z) => (
            <tr key={z.position}>
              <td>{z.position}</td>
              <Zahl>{fmtCent(z.betrag)}</Zahl>
            </tr>
          ))}
          <tr className="font-semibold">
            <td>Gewinn</td>
            <Zahl>{fmtCent(euer.gewinn)}</Zahl>
          </tr>
          <tr>
            <td className="text-muted-foreground">Privat (nicht im Gewinn)</td>
            <Zahl>{fmtCent(euer.privat)}</Zahl>
          </tr>
          <tr>
            <td className="text-muted-foreground">
              Offen ohne Kategorie (nicht im Gewinn)
            </td>
            <Zahl>{fmtCent(euer.offen)}</Zahl>
          </tr>
          <tr>
            <td className="text-muted-foreground">
              Privateinlagen (privat bezahlte Ausgaben)
            </td>
            <Zahl>{fmtCent(euer.einlagen)}</Zahl>
          </tr>
        </Tabelle>
      </Abschnitt>

      <Abschnitt titel={`Prüfliste (${punkte.length})`}>
        {gruppen.length === 0 && (
          <p className="text-sm text-muted-foreground">Keine Prüfpunkte.</p>
        )}
        {gruppen.map((g) => (
          <details key={g.punkt} className="rounded-md border px-3 py-2">
            <summary className="cursor-pointer text-sm font-medium">
              {g.punkt} ({g.liste.length})
            </summary>
            <Tabelle
              kopf={["Datum", "Text", "Kategorie", "Brutto", "Hinweis"]}
              rechts={[3]}
            >
              {g.liste.map((x, i) => (
                <tr key={i}>
                  <td className="whitespace-nowrap">
                    {fmtDatum(x.posten?.datum ?? x.posten?.ustDatum ?? null)}
                  </td>
                  <td>{x.posten?.text}</td>
                  <td>{x.posten?.kategorie}</td>
                  <Zahl>{x.posten ? fmtCent(x.posten.brutto) : ""}</Zahl>
                  <td className="text-xs text-muted-foreground">{x.hinweis}</td>
                </tr>
              ))}
            </Tabelle>
          </details>
        ))}
      </Abschnitt>
    </div>
  );
};
